/**
 * Confirmation before rejecting every candidate for a patient in the review
 * queue. Rejecting keeps the link (status Rejected) and records the reviewer.
 */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useId } from 'react';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  title: {
    id: 'ehrSync.reject.title',
    defaultMessage:
      '{count, plural, one {Reject the candidate} other {Reject all # candidates}} for {name}?',
  },
  body: {
    id: 'ehrSync.reject.body',
    defaultMessage:
      '{count, plural, one {The {vendor} record is not} other {None of the {vendor} records is}} this patient. The link moves to Rejected and nothing syncs.',
  },
  audit: {
    id: 'ehrSync.reject.audit',
    defaultMessage:
      'Your decision is recorded against your account. To try again, enroll the patient again with their external patient ID. That reuses this link and reruns matching.',
  },
  theEhr: { id: 'ehrSync.reject.theEhr', defaultMessage: 'EHR' },
  cancel: { id: 'ehrSync.reject.cancel', defaultMessage: 'Cancel' },
  confirm: { id: 'ehrSync.reject.confirm', defaultMessage: 'Reject' },
});

/** Props for {@link RejectMatchDialog}. */
interface RejectMatchDialogProps {
  open: boolean;
  patientName: string;
  candidateCount: number;
  /** EHR vendor for the wording, e.g. 'Epic'. */
  vendor?: string | null;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Reject confirmation dialog.
 *
 * @param props - see {@link RejectMatchDialogProps}
 */
export default function RejectMatchDialog({
  open,
  patientName,
  candidateCount,
  vendor,
  loading = false,
  onCancel,
  onConfirm,
}: RejectMatchDialogProps) {
  const { formatMessage } = useIntl();
  const count = Math.max(candidateCount, 1);
  const bodyId = useId();

  return (
    <Dialog
      open={open}
      // Escape and backdrop MUST NOT dismiss a reject that is in flight.
      onClose={loading ? undefined : onCancel}
      aria-describedby={bodyId}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        {formatMessage(messages.title, { count, name: patientName })}
      </DialogTitle>
      <DialogContent>
        <Typography id={bodyId} variant="body2" gutterBottom>
          {formatMessage(messages.body, {
            count,
            vendor: vendor ?? formatMessage(messages.theEhr),
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatMessage(messages.audit)}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button
          color="error"
          variant="contained"
          onClick={onConfirm}
          disabled={loading}
        >
          {formatMessage(messages.confirm)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
